/**
 * Composable for chart components.
 * Provides common properties and methods for chart components.
 * Handles chart title, dimensions, margins, hover box, symbols, etc.
 */

import { computed, ref, toValue } from "vue";
import { groupBy } from "lodash-es";
import { extent } from "d3-array";
import { select } from "d3-selection";
import { nice } from "d3";

import { notNull } from "@/helpers/utility/Type";
import { svgToDataURL } from "@/helpers/utility/SVG";
import { mapValues } from "@/helpers/utility/CollectionFunctions";
import { i18n } from "@/i18n";
import { useSVGTextWidthCache } from "@core/composables/useSVGTextWidthCache";
import { drawEvaluationTitle } from "./chartDrawFunction";
import { roundUp } from "@/helpers/utility/MathHelper";

import type { Selection, BaseType } from "d3-selection";
import type { PreSettings } from "@editor/items/charts/charts";
import type { ChartSettings } from "@editor/items/charts/ChartItem";
import type { ColumnEntry, DataEntry, EntryValue } from "@editor/items/charts/ChartData";
import type { MaybeRefOrGetter } from "vue";
import type { SetRequired } from "type-fest";

export interface ChartComponentProps {
  data: DataEntry[];
  id?: string;
  width?: number;
  height?: number;
  yUnit?: string;
  settings?: Partial<ChartSettings> &
    Partial<PreSettings> & { scaleColors?: string[] };
  formattedTitle?: { text: string; style?: string }[];
  animationsDisabled?: boolean;
  titleClickable?: boolean;
  columnData?: Map<string, ColumnEntry>;
  margin?: { top: number; left: number; bottom: number; right: number };
}

type FinalChartComponentProps = SetRequired<
  ChartComponentProps,
  | "id"
  | "width"
  | "height"
  | "settings"
  | "titleClickable"
  | "animationsDisabled"
  | "formattedTitle"
  | "margin"
>;

export type ChartEmitEvents = {
  (e: "hover-on", value: { item: EntryValue; position: { x: number; y: number }; parentItem?: DataEntry; }): void;
  (e: "hover-off"): void;
  (e: "click:title", event: MouseEvent): void;
  (e: "click:column-title", event: {
    uid: string;
    title: string;
    shortTitle: string;
    groupTitle?: string | undefined;
    groupShortTitle?: string | undefined;
    type: string;
    id: string | number;
    withSimulation?: boolean;
  }): void;
  (e: "click:axis-title", event: { axis: "x" | "y"; x: number; y: number }): void;
  (e: "open-round-actions", event: { entry: EntryValue; position: { x: number; y: number } }): void;
};

export function useChartEngine(
  props: MaybeRefOrGetter<FinalChartComponentProps>,
  functions: {
    emit: ChartEmitEvents;
  }
) {
  const { emit } = functions;

  // Values
  const svgElement = ref<SVGSVGElement>();
  const labelsWidth = ref(0);
  const labelsHeight = ref(0);
  const titleHeight = computed(() =>
    toValue(props).settings.titleVisible === false ? 0 : (toValue(props).settings.fontSize || 12) * 3.75
  );

  const svg = ref<Selection<SVGSVGElement, unknown, null, undefined> | null>(
    null
  );
  const container = ref<Selection<
    SVGGElement,
    unknown,
    null,
    undefined
  > | null>(null);
  const animationDuration = ref(0);
  const paddingLeft = ref(0);
  const paddingRight = ref(0);

  const { measureTextWidthCached } = useSVGTextWidthCache(svgElement);

  // COMPUTED VALUES

  const innerWidth = computed(() => {
    const p = toValue(props);
    return (
      p.width -
      p.margin.left -
      p.margin.right -
      paddingLeft.value -
      paddingRight.value
    );
  });
  const innerHeight = computed(() => {
    const p = toValue(props);
    return (
      p.height -
      p.margin.top -
      p.margin.bottom -
      labelsHeight.value -
      titleHeight.value
    );
  });

  const filteredData = computed(() => {
    let data = toValue(props).data;

    if (toValue(props).settings.filterZeroValues) {
      data = data.map((d) => ({
        ...d,
        values: d.values.filter((v) => v.value != 0)
      }));
    }

    return data;
  });

  const yUnits = computed(() => {
    const allUnits = filteredData.value.flatMap((d) => d.values.map((v) => {
      if (v.unit == null) return null;
      return v.unit;
    }));
    return Array.from(new Set(allUnits.filter(notNull)));
  });

  const yUnitWidths = computed(() => {
    const propsValue = toValue(props);
    const labelsVisible = propsValue.settings.yAxisLabelsVisible;
    const fontSize = propsValue.settings.fontSize || 12;
    return yUnits.value.reduce(
      (all, unit) => {
        if (!labelsVisible) return { ...all, [unit]: 0 };
        const domain = unitDomains.value[unit];
        if (domain == null) return all;
        const textLengths = [...domain, 100].map(
          (d) => measureTextWidthCached(String(d), fontSize) + 10
        );
        return { ...all, [unit]: Math.max(...textLengths, 0) };
      },
      {} as Record<string, number>
    );
  });

  const yTicksWidth = computed(() => {
    const fontSize = toValue(props).settings.fontSize || 12;
    const titlesVisible = toValue(props).settings.yAxisTitleVisible;
    const gapsWidth = titlesVisible ? fontSize * 2 : fontSize * 0.8;
    const titlesWidth = (yUnits.value.length - 1) * gapsWidth;
    return (
      yUnits.value.reduce((sum, unit) => {
        return yUnitWidths.value[unit] + sum;
      }, 0) +
      titlesWidth
    );
  });

  /**
   * Get the d.values array that is the longest in all of the data entries.
   * This is needed to set the correct x axis width.
   */
  const longestValuesArray = computed(() => {
    return filteredData.value.reduce(
      (longestValues, d) =>
        d.values.length > longestValues.length ? d.values : longestValues,
      [] as EntryValue[]
    );
  });

  const allValues = computed(() => filteredData.value.flatMap((d) => d.values));

  const valuesByColumnId = computed(() => groupBy(allValues.value, "columnId"));

  /**
   * Total min and max value of the data
   */
  const yExtents = computed(() => {
    if (toValue(props).settings.subValuesVisible) {
      const allSubValues = allValues.value.flatMap((v) =>
        (v.subValues || []).flatMap((v) => v?.yEnd || 0)
      );
      if (allSubValues.length > 0) {
        return extent(allSubValues) as [number, number];
      }
    }

    return extent([
      0,
      ...filteredData.value.flatMap((d) =>
        d.values.flatMap((c) => [roundUp(c.startValue || 0), roundUp(c.value || 0)])
      )
    ]) as [number, number];
  });

  const domain = computed(() => {
    if (toValue(props).settings.zoomed) return yExtents.value;
    return toValue(props).settings?.range ?? yExtents.value;
  });

  const valuesByUnit = computed(() => {
    const result: { [color: string]: EntryValue[] } = {};

    for (const value of allValues.value) {
      if (!(value.unit in result)) result[value.unit] = [];
      result[value.unit].push(value);
    }

    return result;
  });

  /**
   * Colors of every unit.
   * The unit only has a single color if ALL of the included
   * entries have the same color.
   */
  const unitColors = computed(() => {
    return mapValues(valuesByUnit.value, (entryValues) => {
      if (entryValues.length == 0) return;
      const color = entryValues[0].color;
      for (let i = 1; i < entryValues.length; i++) {
        if (entryValues[i].color != color) return;
      }
      return color;
    });
  });

  const unitDomains = computed(() => {
    return mapValues(valuesByUnit.value, (entryValues, unit) => {
      const allValues = entryValues.flatMap((v) => [
        v.value || 0,
        v.startValue || 0
      ]);

      const yExtents = extent(allValues) as [number, number];

      if (toValue(props).settings.subValuesVisible) {
        const allSubValues = entryValues.flatMap((v) =>
          (v.subValues || []).flatMap((v) => v?.yEnd || 0)
        );
        if (allSubValues.length > 0) {
          const subExtents = extent(allSubValues) as [number, number];
          yExtents[0] = subExtents[0];
          yExtents[1] = subExtents[1];
        }
      }

      yExtents[0] = Math.floor(yExtents[0]);
      yExtents[1] = Math.ceil(yExtents[1]);

      if (!toValue(props).settings.zoomed) {
        yExtents[0] = Math.min(yExtents[0], 0);
        yExtents[1] = Math.max(yExtents[1], 0);

        if (unit == "%") {
          yExtents[0] = Math.min(yExtents[0], 0);
          yExtents[1] = Math.max(yExtents[1], 100);
        }
      }

      if (toValue(props).settings.centered) {
        const maxValue = Math.max(Math.abs(yExtents[0]), Math.abs(yExtents[1]));
        yExtents[0] = -maxValue;
        yExtents[1] = maxValue;
      }

      if (yExtents[0] == -Infinity) yExtents[0] = Number.MIN_SAFE_INTEGER;
      if (yExtents[1] == Infinity) yExtents[1] = Number.MAX_SAFE_INTEGER;

      return nice(yExtents[0], yExtents[1], 5);
    });
  });

  // METHODS

  function drawTitle() {
    if (!svgElement.value) return;

    if (toValue(props).settings.titleVisible == false) {
      if (svg.value) svg.value.select(".chart__header").remove();
      return;
    }

    const header = drawEvaluationTitle(
      svgElement.value,
      toValue(props).width,
      toValue(props).formattedTitle,
      (toValue(props).settings.fontSize || 12) + 4
    );

    if (toValue(props).titleClickable) {
      header.classed("chart__header--clickable", true);
      header.on("click", (event) => emit("click:title", event));
    }
  }

  function getAsDataURL() {
    const srcSvgElement = svgElement.value || null;
    srcSvgElement?.classList.add("-print");
    const url = svgToDataURL(srcSvgElement);
    srcSvgElement?.classList.remove("-print");

    return url;
  }

  function updateHoverBox(
    position: { x: number; y: number },
    item: EntryValue,
    parentItem?: DataEntry
  ) {
    emit("hover-on", {
      item,
      parentItem,
      position
    });
  }

  function closeHoverBox() {
    emit("hover-off");
  }

  function appendSymbol<
    E extends BaseType,
    T extends EntryValue & { parentItem?: DataEntry },
    P extends BaseType,
    PD extends { key: string }
  >(group: Selection<E, T, P, PD>) {
    // create a group for each symbol
    const symbolGroup = group
      .append("g")
      .attr("class", "chart__symbol");
    // to remove: click on symbol
    // .on("click", function () {
    //   this.classList.toggle("-selected");
    // });

    // create a filled symbol in every group
    symbolGroup
      .append("use")
      .attr("width", 70)
      .attr("height", 70)
      .attr("x", -35)
      .attr("y", -35)
      .attr("class", "chart__symbol-fill");

    symbolGroup
      .append("use")
      .attr("width", 70)
      .attr("height", 70)
      .attr("x", -35)
      .attr("y", -35)
      .attr("class", "chart__symbol-white");

    // create a symbol stroke in every group
    symbolGroup
      .append("use")
      .attr("width", 70)
      .attr("height", 70)
      .attr("x", -35)
      .attr("y", -35)
      .attr("class", "chart__symbol-stroke");

    // create the text for every group
    symbolGroup
      .append("text")
      .attr("class", "chart__symbol-text")
      .attr("y", 5)
      .attr("x", 0)
      .text((d) => formatNumber(d.value, d.numberFormat || "value") + d.unit);

    symbolGroup.on("mousemove", (event: MouseEvent, v) => {
      const x = event.clientX;
      const y = event.clientY;

      updateHoverBox({ x, y }, v, v.parentItem);

      if (svg.value) {
        svg.value.on("mousemove.symbol", (event) => {
          const group = (event.target as Element)?.closest(
            ".chart__symbol"
          );

          if (group == null) {
            closeHoverBox();
            if (svg.value) {
              svg.value.on("mousemove.symbol", null);
            }
          }
        });
      }
    });

    return symbolGroup;
  }

  function formatNumber(number: number | null | undefined, format: string) {
    if (number == null) return "";
    if (isNaN(number)) return "?";
    if (number === Infinity) return "∞";
    if (number === -Infinity) return "-∞";
    return i18n.n(number, format);
  }

  function updateSymbol<
    E extends Element,
    T extends {
      value: number | null;
      symbol?: DataEntry["symbol"];
      numberFormat?: string;
      unit?: string;
    },
    PD extends { key: string }
  >(symbolGroup: Selection<E, T, SVGElement, PD>) {
    symbolGroup.each(function (d) {
      const group = select(this);
      const symbol = d.symbol;

      const node = this as WithSymbolData<typeof this>;
      if (node.__symbol != symbol) {
        node.__symbol = symbol;

        // Update symbols:
        group
          .select(".chart__symbol-fill")
          .attr("href", `#svg-symbols-${symbol}-filled`);
        group
          .select(".chart__symbol-white")
          .attr("href", `#svg-symbols-${symbol}-filled`);
        group
          .select(".chart__symbol-stroke")
          .attr("href", `#svg-symbols-${symbol}`);
      }

      const text = formatNumber(d.value, d.numberFormat || "value") + d.unit;

      if (node.__text != text) {
        node.__text = text;
        group.select(".chart__symbol-text").text(text);
      }
    });

    return symbolGroup;
  }

  type WithSymbolData<T extends Node> = T & {
    __symbol: string | undefined;
    __text: string | undefined;
  };

  function getScreenPosition(
    localPosition: { x: number; y: number }
  ) {
    if (!svg.value) return { x: 0, y: 0 };
    
    const svgNode = svg.value.node();
    if (!svgNode) return { x: 0, y: 0 };

    const point = svgNode.createSVGPoint();
    point.x = localPosition.x;
    point.y = localPosition.y;

    const ctm = svgNode.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };

    const screenPoint = point.matrixTransform(ctm);
    return { x: screenPoint.x, y: screenPoint.y };
  }

  /**
   * Convert screen coordinates to local SVG coordinates
   * @param screenPosition The position in screen coordinates
   * @param svgNode SVG Element or g element in SVG
   * @returns 
   */
  function getLocalPosition(
    screenPosition: { x: number; y: number },
    svgNode: SVGGraphicsElement
  ) {
    const svgOwnerSVG = svgNode.ownerSVGElement;
    if (!svgOwnerSVG) return { x: 0, y: 0 };

    const point = svgOwnerSVG.createSVGPoint();
    point.x = screenPosition.x;
    point.y = screenPosition.y;

    const ctm = svgNode.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };

    const localPoint = point.matrixTransform(ctm.inverse());
    return { x: localPoint.x, y: localPoint.y };
  }

  return {
    animationDuration,
    svg,
    container,
    svgElement,

    titleHeight,
    labelsWidth,
    labelsHeight,
    innerWidth,
    innerHeight,
    paddingLeft,
    paddingRight,

    yUnits,
    yTicksWidth,
    yUnitWidths,
    filteredData,
    allValues,
    valuesByColumnId,
    longestValuesArray,
    valuesByUnit,

    yExtents,
    domain,

    unitColors,
    unitDomains,

    drawTitle,
    getAsDataURL,
    updateHoverBox,
    closeHoverBox,
    measureTextWidthCached,
    updateSymbol,
    appendSymbol,
    getScreenPosition,
    getLocalPosition
  };
}
